import React, { useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { Row, Col, Card, Button, Table, Badge } from 'react-bootstrap';
import { 
  ArrowLeft, Edit, Download, FileSpreadsheet, Send, 
  Layers, Cpu, Sliders, FileText, Image as ImageIcon 
} from 'lucide-react';
import toast from 'react-hot-toast';
import Swal from 'sweetalert2';

import Header from '../Include/Header';
import Sidebar from '../Include/Sidebar';
import Footer from '../Include/Footer';
import Loader from '../Include/Loader';

import { getAdminOneBom } from '../../../Store/Admin/believe/bom/GetOneBom';
import { downloadAdminBomPdf } from '../../../Store/Admin/believe/bom/DownloadBomPdf';
import { exportAdminBomExcel } from '../../../Store/Admin/believe/bom/ExportBomExcel';
import { generatePrFromAdminBom } from '../../../Store/Admin/believe/bom/GeneratePrBom';
import { formatINR } from '../../../helper/bom';

const ViewBom = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);

  useEffect(() => {
    if (id) {
      dispatch(getAdminOneBom(id));
    }
  }, [id]);

  const { data: bomResponse, loading } = useSelector((state) => state?.getAdminOneBom || {});
  const bom = bomResponse?.data;

  const handleDownloadPdf = () => {
    if (!bom?._id) return;
    toast.loading("Generating PDF...", { id: "pdf-view-toast" });
    dispatch(downloadAdminBomPdf(bom._id)).then((res) => {
      toast.dismiss("pdf-view-toast");
      if (res?.payload?.success && res?.payload?.data?.url) {
        window.open(res.payload.data.url, "_blank");
      }
    });
  };

  const handleExportExcel = () => {
    if (!bom?._id) return;
    toast.loading("Exporting Excel...", { id: "excel-view-toast" });
    dispatch(exportAdminBomExcel(bom._id)).then((res) => {
      toast.dismiss("excel-view-toast");
      if (res?.payload?.success && res?.payload?.data?.url) {
        window.open(res.payload.data.url, "_blank");
      }
    });
  };

  const handleGeneratePr = () => {
    if (!bom?._id) return;
    Swal.fire({
      title: "Generate Store PR?",
      text: `Create Store Purchase Request for ${bom.bom_no}?`,
      icon: "question",
      showCancelButton: true,
      confirmButtonText: "Yes, Generate PR"
    }).then((result) => {
      if (result.isConfirmed) {
        dispatch(generatePrFromAdminBom(bom._id)).then((res) => {
          if (res?.payload?.success) {
            toast.success("Purchase Request generated successfully!");
            dispatch(getAdminOneBom(bom._id));
          }
        });
      }
    });
  };

  if (loading || !bom) {
    return (
      <div className="main-wrapper">
        <Header handleOpen={() => {}} />
        <Sidebar />
        <div className="page-wrapper"><div className="content"><Loader /></div></div>
      </div>
    );
  }

  return (
    <div className={`main-wrapper ${isSidebarOpen ? "slide-nav" : ""}`}>
      <Header handleOpen={() => setIsSidebarOpen(!isSidebarOpen)} />
      <Sidebar />
      <div className="page-wrapper">
        <div className="content" style={{ fontFamily: '"Times New Roman", Times, serif' }}>
          {/* Header Actions */}
          <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
            <div className="d-flex align-items-center gap-2">
              <Button variant="outline-secondary" size="sm" onClick={() => navigate('/admin/bom-management')} className="rounded-2">
                <ArrowLeft size={14} className="me-1" /> Back
              </Button>
              <h4 className="mb-0 fw-bold text-primary">{bom.bom_no}</h4>
              {bom.version > 0 && <Badge bg="secondary">Rev {bom.version}</Badge>}
              <Badge bg="success">{bom.status}</Badge>
              {bom.pr_generated && <Badge bg="info">PR: {bom.generated_pr_voucher}</Badge>}
            </div>

            <div className="d-flex align-items-center gap-2">
              <Button variant="outline-primary" size="sm" onClick={() => navigate('/admin/manage-bom', { state: { id: bom._id } })}>
                <Edit size={14} className="me-1" /> Edit BOM
              </Button>
              <Button variant="outline-dark" size="sm" onClick={handleDownloadPdf}>
                <Download size={14} className="me-1" /> Download PDF
              </Button>
              <Button variant="outline-success" size="sm" onClick={handleExportExcel}>
                <FileSpreadsheet size={14} className="me-1" /> Export Excel
              </Button>
              {!bom.pr_generated && (
                <Button variant="primary" size="sm" onClick={handleGeneratePr}>
                  <Send size={14} className="me-1" /> Generate Store PR
                </Button>
              )}
            </div>
          </div>

          {/* Info Card */}
          <Card className="shadow-sm border-0 rounded-3 mb-3">
            <Card.Body className="p-3">
              <Row className="g-3">
                <Col md={4} className="border-end">
                  <span className="small text-muted d-block">PROPOSAL SUBJECT</span>
                  <strong className="fs-6 text-dark">{bom.subject || '-'}</strong>
                  <div className="small text-muted mt-1">Date: {bom.date ? new Date(bom.date).toLocaleDateString('en-GB') : '-'}</div>
                  {bom.quotation_ref_no && (
                    <div className="small text-muted">Ref: {bom.quotation_ref_no}</div>
                  )}
                </Col>
                <Col md={4} className="border-end">
                  <span className="small text-muted d-block">CLIENT / CUSTOMER</span>
                  <strong className="text-dark">{bom.client_snapshot?.name || '-'}</strong>
                  {bom.client_snapshot?.attention_person && (
                    <div className="small text-muted">Attn: {bom.client_snapshot.attention_person}</div>
                  )}
                  {bom.client_snapshot?.city && (
                    <div className="small text-muted">{bom.client_snapshot.city}, {bom.client_snapshot.state} | GSTIN: {bom.client_snapshot.gst_no || '-'}</div>
                  )}
                </Col>
                <Col md={4}>
                  <span className="small text-muted d-block">COMMERCIAL SUMMARY</span>
                  <div className="fs-5 fw-bold text-success">{formatINR(bom.grand_total)}</div>
                  <div className="small text-muted">Taxable: {formatINR(bom.taxable_amount)} | Total Tax: {formatINR(bom.total_tax)}</div>
                </Col>
              </Row>
            </Card.Body>
          </Card>

          {/* Annexure 1: Techno-Commercial Summary */}
          <Card className="shadow-sm border-0 rounded-3 mb-3">
            <Card.Header className="bg-light py-2">
              <h6 className="mb-0 fw-bold text-dark">Annexure 1: Techno-Commercial Offer</h6>
            </Card.Header>
            <Card.Body className="p-0">
              <Table responsive hover bordered className="mb-0 align-middle" style={{ fontSize: "12px" }}>
                <thead className="table-light">
                  <tr>
                    <th style={{ width: "5%" }} className="text-center">Sr.</th>
                    <th style={{ width: "50%" }}>Assembly / Panel Description</th>
                    <th style={{ width: "15%" }} className="text-center">Quantity</th>
                    <th style={{ width: "15%" }} className="text-end">Rate / Unit</th>
                    <th style={{ width: "15%" }} className="text-end">Total Price</th>
                  </tr>
                </thead>
                <tbody>
                  {(bom.assemblies || []).map((ass, idx) => (
                    <tr key={idx}>
                      <td className="text-center fw-bold">{idx + 1}</td>
                      <td><strong>{ass.assembly_name}</strong> ({ass.assembly_type})</td>
                      <td className="text-center">{ass.quantity} {ass.unit}</td>
                      <td className="text-end">{formatINR(ass.unit_rate)}</td>
                      <td className="text-end fw-bold">{formatINR(ass.total_price)}</td>
                    </tr>
                  ))}
                  <tr className="table-light fw-bold">
                    <td colSpan="2" className="text-end">TOTAL QUANTITY:</td>
                    <td className="text-center">{bom.total_assemblies_count}</td>
                    <td className="text-end">SUBTOTAL:</td>
                    <td className="text-end">{formatINR(bom.subtotal_amount)}</td>
                  </tr>
                  {bom.packaging_charges > 0 && (
                    <tr>
                      <td colSpan="4" className="text-end text-muted">Packaging Charges ({bom.packaging_gst_percent}% GST):</td>
                      <td className="text-end">{formatINR(bom.packaging_charges)}</td>
                    </tr>
                  )}
                  {bom.loading_charges > 0 && (
                    <tr>
                      <td colSpan="4" className="text-end text-muted">Loading Charges ({bom.loading_gst_percent}% GST):</td>
                      <td className="text-end">{formatINR(bom.loading_charges)}</td>
                    </tr>
                  )}
                  {bom.freight_charges > 0 && (
                    <tr>
                      <td colSpan="4" className="text-end text-muted">Freight Charges ({bom.freight_gst_percent}% GST):</td>
                      <td className="text-end">{formatINR(bom.freight_charges)}</td>
                    </tr>
                  )}
                  <tr className="fw-bold table-light">
                    <td colSpan="4" className="text-end">TAXABLE AMOUNT:</td>
                    <td className="text-end">{formatINR(bom.taxable_amount)}</td>
                  </tr>
                  <tr>
                    <td colSpan="4" className="text-end text-muted">TOTAL GST:</td>
                    <td className="text-end">{formatINR(bom.total_tax)}</td>
                  </tr>
                  <tr className="table-primary fw-bold fs-6">
                    <td colSpan="4" className="text-end text-primary">GRAND TOTAL:</td>
                    <td className="text-end text-primary">{formatINR(bom.grand_total)}</td>
                  </tr>
                </tbody>
              </Table>
            </Card.Body>
          </Card>

          {/* Annexure 2: Hierarchical BOM & Components */}
          <Card className="shadow-sm border-0 rounded-3 mb-3">
            <Card.Header className="bg-light py-2">
              <h6 className="mb-0 fw-bold text-dark">Annexure 2: Hierarchical Bill of Materials</h6>
            </Card.Header>
            <Card.Body className="p-3">
              {(bom.assemblies || []).map((assembly, aIdx) => (
                <div key={aIdx} className="border rounded-3 p-3 mb-3 bg-light">
                  <div className="d-flex justify-content-between align-items-center mb-2 pb-2 border-bottom">
                    <h6 className="mb-0 fw-bold text-primary">
                      {aIdx + 1}. {assembly.assembly_name}
                    </h6>
                    <Badge bg="dark">BOARD QTY: {assembly.quantity}</Badge>
                  </div>

                  {(assembly.sub_assemblies || []).map((sub, sIdx) => (
                    <div key={sIdx} className="bg-white rounded border p-2 mb-2">
                      <div className="d-flex justify-content-between align-items-center mb-1 pb-1 border-bottom">
                        <strong className="text-dark" style={{ fontSize: "12px" }}>{sub.code} - {sub.name}</strong>
                        <Badge bg="warning" text="dark">FEEDER QTY: {sub.quantity}</Badge>
                      </div>

                      <Table responsive size="sm" bordered hover className="mb-0 align-middle" style={{ fontSize: "11px" }}>
                        <thead className="table-light">
                          <tr>
                            <th style={{ width: "4%" }} className="text-center">Sr.</th>
                            <th style={{ width: "40%" }}>BOQ Description</th>
                            <th style={{ width: "16%" }}>Make / Brand</th>
                            <th style={{ width: "12%" }} className="text-center">Unit</th>
                            <th style={{ width: "12%" }} className="text-center">Qty / Step</th>
                            <th style={{ width: "16%" }} className="text-center">Total Quantity</th>
                          </tr>
                        </thead>
                        <tbody>
                          {(sub.items || []).map((item, iIdx) => (
                            <tr key={iIdx}>
                              <td className="text-center fw-bold">{iIdx + 1}</td>
                              <td>
                                <strong>{item.item_name}</strong>
                                {item.description && <div className="text-muted small">{item.description}</div>}
                              </td>
                              <td>{item.make || "-"}</td>
                              <td className="text-center">{item.unit}</td>
                              <td className="text-center">{item.unit_quantity}</td>
                              <td className="text-center fw-bold bg-light">{item.total_quantity}</td>
                            </tr>
                          ))}
                        </tbody>
                      </Table>
                    </div>
                  ))}

                  {/* Specifications */}
                  {assembly.specifications && assembly.specifications.length > 0 && (
                    <div className="mt-3">
                      <span className="fw-bold small text-muted d-block mb-1" style={{ fontSize: "11px" }}>Technical Specification Matrix</span>
                      <Row className="g-2">
                        {assembly.specifications.map((group, gIdx) => (
                          <Col md={6} key={gIdx}>
                            <div className="p-2 border rounded bg-white h-100">
                              <strong className="small text-primary d-block mb-1" style={{ fontSize: "11px" }}>{group.group_code}. {group.group_title}</strong>
                              <Table size="sm" className="mb-0" style={{ fontSize: "10.5px" }}>
                                <tbody>
                                  {(group.parameters || []).map((p, pIdx) => (
                                    <tr key={pIdx}>
                                      <td className="text-muted" style={{ width: "40%" }}>{p.parameter_name}</td>
                                      <td style={{ width: "30%" }}>{p.details || '-'}</td>
                                      <td className="fw-semibold" style={{ width: "30%" }}>{p.type_value || '-'} {p.unit || ''}</td>
                                    </tr>
                                  ))}
                                </tbody>
                              </Table>
                            </div>
                          </Col>
                        ))}
                      </Row>
                    </div>
                  )}
                </div>
              ))}
            </Card.Body>
          </Card>

          {/* Annexure 3: Terms & Drawing */}
          <Row className="g-3">
            <Col md={7}>
              <Card className="shadow-sm border-0 rounded-3 h-100">
                <Card.Header className="bg-light py-2">
                  <h6 className="mb-0 fw-bold text-dark">Annexure 3: General Terms & Conditions</h6>
                </Card.Header>
                <Card.Body className="p-3" style={{ fontSize: "11.5px" }}>
                  {(bom.terms_and_conditions || []).map((term, idx) => (
                    <div key={idx} className="mb-2">
                      <strong>{idx + 1}. {term.clause_title}:</strong> {term.clause_content}
                    </div>
                  ))}
                </Card.Body>
              </Card>
            </Col>

            <Col md={5}>
              <Card className="shadow-sm border-0 rounded-3 h-100">
                <Card.Header className="bg-light py-2">
                  <h6 className="mb-0 fw-bold text-dark">GA Drawing Reference</h6>
                </Card.Header>
                <Card.Body className="p-3" style={{ fontSize: "11.5px" }}>
                  <div><strong>Drawing No:</strong> {bom.drawing_details?.drawing_no || "-"}</div>
                  <div><strong>Title:</strong> {bom.drawing_details?.title || "-"}</div>
                  <div><strong>Revision:</strong> {bom.drawing_details?.revision || "00"}</div>
                  <div className="mt-2 p-2 bg-light rounded">
                    <strong>Dimensions (H × W × D):</strong><br />
                    {bom.drawing_details?.dimensions?.height || '-'}mm (H) × {bom.drawing_details?.dimensions?.width || '-'}mm (W) × {bom.drawing_details?.dimensions?.depth || '-'}mm (D)
                    <br />
                    Base Stand: {bom.drawing_details?.dimensions?.base_height || '-'}mm
                  </div>
                  {bom.drawing_details?.notes && (
                    <div className="mt-2 small text-muted"><strong>Notes:</strong> {bom.drawing_details.notes}</div>
                  )}
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </div>
        <Footer />
      </div>
    </div>
  );
};

export default ViewBom;
